import { z } from "zod";
import { SchemaError } from "./errors.ts";

/** Inline markup string: **bold**, *italic*, `code`, [text](https://…), [text](doc:CODE), [[Label]]; "\" escapes * ` [ ] \ */
export type Inline = string;
export type CodeLine = { type: "code"; text: string };
export type Cell = Inline | Array<Inline | CodeLine>;

export type Block =
  | { type: "h1"; title: string; number?: false }
  | { type: "h2"; title: string; number?: false }
  | { type: "p"; text: Inline }
  | { type: "kv"; pairs: Array<[string, Inline]> }
  | { type: "bullets"; items: Inline[] }
  | { type: "steps"; items: Inline[] }
  | { type: "table"; columns: Inline[]; widths?: number[]; rows: Cell[][]; firstColBold?: boolean }
  | { type: "groupTable"; groups: Array<{ title: string; meta?: string; items: Cell[] }> }
  | { type: "code"; text: string }
  | { type: "note"; text: Inline; title?: string }
  | { type: "warning"; text: Inline; title?: string }
  | { type: "pageBreak" }
  | { type: "image"; src: string; caption?: string; alt?: string };

/** Attachment reference accepted by image blocks: "att:" + lowercase sha256. */
export const ATTACHMENT_SRC_RE = /^att:[0-9a-f]{64}$/;

/** C0 control characters that are invalid in .docx XML. TAB, LF and CR are not included (they have their own rules). */
// biome-ignore lint/suspicious/noControlCharactersInRegex: matching control characters is the point.
export const CONTROL_CHAR_RE = /[\u0000-\u0008\u000B\u000C\u000E-\u001F]/;

/** A string without C0 control characters (C1): every string field of blocks and DocMeta uses it. */
export const safeString = () =>
  z.string().refine((s) => !CONTROL_CHAR_RE.test(s), "control characters are not allowed");

const singleLine = (what: string) =>
  safeString().refine((s) => !/[\r\n]/.test(s), `${what} cannot contain line breaks; use one block per line`);

export const InlineSchema = safeString();
export const CodeLineSchema = z.strictObject({ type: z.literal("code"), text: singleLine("a code line") });
export const CellSchema = z.union([InlineSchema, z.array(z.union([InlineSchema, CodeLineSchema])).min(1)]);

const heading = <T extends "h1" | "h2">(type: T) =>
  z.strictObject({ type: z.literal(type), title: singleLine("a heading"), number: z.literal(false).optional() });

const TableSchema = z
  .strictObject({
    type: z.literal("table"),
    columns: z.array(InlineSchema).min(1),
    widths: z.array(z.number().positive()).optional(),
    rows: z.array(z.array(CellSchema)),
    firstColBold: z.boolean().optional(),
  })
  .superRefine((t, ctx) => {
    if (t.widths && t.widths.length !== t.columns.length) {
      ctx.addIssue({
        code: "custom",
        path: ["widths"],
        message: `widths has ${t.widths.length} entries, expected ${t.columns.length}`,
      });
    }
    t.rows.forEach((row, i) => {
      if (row.length !== t.columns.length) {
        ctx.addIssue({
          code: "custom",
          path: ["rows", i],
          message: `row has ${row.length} cells, expected ${t.columns.length}`,
        });
      }
    });
  });

export const BlockSchema: z.ZodType<Block> = z.discriminatedUnion("type", [
  heading("h1"),
  heading("h2"),
  z.strictObject({ type: z.literal("p"), text: InlineSchema }),
  z.strictObject({ type: z.literal("kv"), pairs: z.array(z.tuple([safeString(), InlineSchema])).min(1) }),
  z.strictObject({ type: z.literal("bullets"), items: z.array(InlineSchema).min(1) }),
  z.strictObject({ type: z.literal("steps"), items: z.array(InlineSchema).min(1) }),
  TableSchema,
  z.strictObject({
    type: z.literal("groupTable"),
    groups: z
      .array(z.strictObject({ title: safeString(), meta: safeString().optional(), items: z.array(CellSchema).min(1) }))
      .min(1),
  }),
  z.strictObject({ type: z.literal("code"), text: singleLine("a code block") }),
  z.strictObject({ type: z.literal("note"), text: InlineSchema, title: safeString().optional() }),
  z.strictObject({ type: z.literal("warning"), text: InlineSchema, title: safeString().optional() }),
  z.strictObject({ type: z.literal("pageBreak") }),
  z.strictObject({
    type: z.literal("image"),
    src: z.string().regex(ATTACHMENT_SRC_RE, 'src must be "att:<sha256>"'),
    caption: safeString().optional(),
    alt: safeString().optional(),
  }),
]);

export const BlocksSchema: z.ZodType<Block[]> = z.array(BlockSchema);

/** "[3].rows[0]" style path for a zod issue path. */
export function formatPath(path: ReadonlyArray<PropertyKey>): string {
  return path
    .map((p) => (typeof p === "number" ? `[${p}]` : `.${String(p)}`))
    .join("")
    .replace(/^\./, "");
}

/** Validate a Block[] and return it typed, or throw SchemaError naming the first problem. */
export function assertBlocks(input: unknown): Block[] {
  const result = BlocksSchema.safeParse(input);
  if (result.success) return result.data;
  const issue = result.error.issues[0];
  throw new SchemaError(issue?.message ?? "invalid blocks", formatPath(["blocks", ...(issue?.path ?? [])]));
}
